import type {
  LeafPrimitive,
  Placement,
  Zone,
  ZoneStyle,
} from '@tsagko/label-contracts'
import { computeZoneLayout } from '../canvas/layout'
import { extractVariableName } from './variables-ops'

export interface LeafRegionInfo {
  path: string[]
  zoneId: string
  primitive: LeafPrimitive
  placement?: Placement
  style?: ZoneStyle
  /** Primary (first) referenced variable, for the region table's name column. */
  variableName?: string
  /** Every variable this primitive references (unique, first-occurrence order). */
  variableNames: string[]
  widthMm: number
  heightMm: number
}

/**
 * Walks a layout tree and returns a Map keyed by zone id to the source
 * `Zone` node, so callers can look up zone-level fields (style, split) that
 * the `LaidOutZone` shape doesn't carry.
 */
function indexZonesById(layout: Zone): Map<string, Zone> {
  const out = new Map<string, Zone>()
  function recurse(z: Zone) {
    out.set(z.id, z)
    if (z.children) {
      recurse(z.children[0])
      recurse(z.children[1])
    }
  }
  recurse(layout)
  return out
}

/**
 * Walks a layout under known block dimensions and returns one record per
 * leaf zone in document order. Skips zones whose component is a
 * baked-block-instance (those don't appear in a block's own layout).
 */
export function collectRegions(
  layout: Zone,
  block: { widthMm: number; heightMm: number }
): LeafRegionInfo[] {
  const laid = computeZoneLayout(layout, block)
  const byId = indexZonesById(layout)
  const out: LeafRegionInfo[] = []
  for (const z of laid) {
    if (!z.component) continue
    if (z.component.type === 'baked-block-instance') continue
    const primitive = z.component as LeafPrimitive
    const sourceZone = byId.get(z.id)
    const names = variableNamesForLeaf(primitive)
    out.push({
      path: z.path,
      zoneId: z.id,
      primitive,
      placement: 'placement' in primitive ? primitive.placement : undefined,
      style: sourceZone?.style,
      variableName: names[0],
      variableNames: names,
      widthMm: z.w,
      heightMm: z.h,
    })
  }
  return out
}

/**
 * Every variable name a leaf primitive references, unique and in
 * first-occurrence order. Covers `text` and `symbol` (raw/gtin/url single
 * values + each gs1-ai element).
 *
 * `localized-text`/`localized-rich-text` are intentionally NOT handled here
 * (deferred follow-up); the prior code didn't handle them either, so there is
 * no regression.
 */
export function variableNamesForLeaf(p: LeafPrimitive): string[] {
  if (p.type === 'text') {
    const n = extractVariableName(p.value.content)
    return n ? [n] : []
  }
  if (p.type === 'symbol') {
    const ds = p.dataStructure
    if (ds.kind === 'raw' || ds.kind === 'gtin' || ds.kind === 'url') {
      const n = extractVariableName(ds.value.content)
      return n ? [n] : []
    }
    if (ds.kind === 'gs1-ai') {
      const out: string[] = []
      for (const el of ds.elements) {
        const n = extractVariableName(el.value.content)
        if (n && !out.includes(n)) out.push(n)
      }
      return out
    }
  }
  return []
}

/** Maps each variable name to the list of leaf paths that reference it. */
export function collectVariableRefs(
  layout: Zone,
  block: { widthMm: number; heightMm: number }
): Record<string, string[][]> {
  const regions = collectRegions(layout, block)
  const out: Record<string, string[][]> = {}
  for (const r of regions) {
    for (const name of r.variableNames) {
      if (!out[name]) out[name] = []
      out[name].push(r.path)
    }
  }
  return out
}
